import { Check, X } from "lucide-react";
import { ghl } from "../../enums";
import { cx } from "../../utils";

const OPTIONS = Object.values(ghl.AppFeatures);

/**
 * Multi-select over the fixed set of things an app can plug into in
 * the CRM. Stored on Apps.featuresInclude as an array of the labels.
 *
 * Values already on a record that are not in the current list are still shown,
 * as removable chips, so saving an older app never silently drops data that was
 * entered before the list existed.
 */
const FeatureSelect = ({ value = [], onChange }) => {
  const selected = Array.isArray(value) ? value : [];

  // Older records stored free text (channel names, etc.) here.
  const legacy = selected.filter(
    (v) => typeof v === "string" && !OPTIONS.includes(v),
  );

  const toggle = (feature) =>
    onChange(
      selected.includes(feature)
        ? selected.filter((f) => f !== feature)
        : [...selected, feature],
    );

  const removeLegacy = (v) => onChange(selected.filter((f) => f !== v));

  return (
    <div>
      <div className="grid gap-2 sm:grid-cols-2">
        {OPTIONS.map((feature) => {
          const isOn = selected.includes(feature);
          return (
            <button
              key={feature}
              type="button"
              onClick={() => toggle(feature)}
              aria-pressed={isOn}
              className={cx(
                "flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-all",
                isOn
                  ? "border-brand-300 bg-brand-50/60 ring-1 ring-brand-200"
                  : "border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50",
              )}
            >
              <span
                className={cx(
                  "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors",
                  isOn
                    ? "border-brand-600 bg-brand-600 text-white"
                    : "border-ink-300 bg-white",
                )}
              >
                {isOn && <Check size={11} strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span
                  className={cx(
                    "block text-[13px] font-medium",
                    isOn ? "text-brand-900" : "text-ink-800",
                  )}
                >
                  {feature}
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-ink-500">
                  {ghl.AppFeatureHints[feature]}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {legacy.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
          <p className="text-xs font-medium text-amber-900">
            Not in the current list
          </p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-amber-700">
            Saved before these options were fixed. They still show on the public page — remove
            any you no longer want.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {legacy.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded-md bg-white py-1 pl-2.5 pr-1 text-[12px] font-medium text-amber-900 ring-1 ring-amber-200"
              >
                {v}
                <button
                  type="button"
                  onClick={() => removeLegacy(v)}
                  className="grid h-5 w-5 place-items-center rounded text-amber-500 hover:bg-amber-100 hover:text-amber-800"
                  aria-label={`Remove ${v}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-ink-400">
        {selected.length} selected
      </p>
    </div>
  );
};

export default FeatureSelect;
